import React, { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { FormControl, Popper, PopperProps } from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import { AutocompleteGetTagProps } from '@material-ui/lab';
import { Check, ChevronDown, ChevronUp } from 'mdi-material-ui';
import classNames from 'clsx';

import { ControlContainer } from 'app/components/ControlContainer';
import { NewCarSelectControlProps } from './NewCarSelectControl.interface';
import { StepSelectControl } from 'app/components/StepSelectControl';
import { FieldsEnum, FormsEnum, LabelEnum } from 'app/shared/enums';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { FormRowValidationMessage } from 'app/components/FormRowValidationMessage';
import { useNewCarSelect } from 'app/components/NewCarSelectControl/useNewCarSelect';
import { CarSelectControlTooltip } from 'app/components/CarSelectControlTooltip';
import { Dictionary } from 'app/services/dict.service';
import { TagProps } from 'app/components/StepSelectControl/StepSelectControl.interface';
import { Badge } from 'app/components/Badge';
import { useStyles } from './NewCarSelectControl.styles';
import { useNewCarSelectScroll } from 'app/components/NewCarSelectControl/useNewCarSelectControlScroll';
import { CepikFormRowContainer } from 'app/components/CepikFormRowContainer';
import { DefaultFormRowContainer } from 'app/components/DefaultFormRowContainer';
import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';
import isSafariOrIOS from 'app/shared/helpers/isSafariOrIos';
import { CarDetailsEtList } from 'app/components/CarDetailsEtList';
import { useVehicleType } from 'app/shared/hooks/useVehicleType';
import { useUser } from 'app/shared/hooks/useUser';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';
import { selectIsCepik3PathActive } from 'app/store/selectors/selectIsCepik3PathActive';

export default function NewCarSelectControl(props: NewCarSelectControlProps) {
  const { formName, className, cepikModalControl } = props;

  const classes = useStyles();
  const { isMotor } = useVehicleType();
  const { isOperator } = useUser();
  const isCepik3ShortPath = useSelector(selectIsCepik3PathActive);
  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);

  const {
    activeQuestion,
    error,
    everyFieldInitialized,
    groupBy,
    loading,
    message,
    messages,
    onBlur,
    onChange,
    onOpen,
    onTagClick,
    onTagFocus,
    open,
    options,
    question,
    value,
    isLastValueSelected,
    popperAnchorRef,
    popperAnchorNode,
    showFillPreviousQuestionsMessage,
    hideHorsePowerOptions,
    groupExpanded,
    setGroupExpanded,
    getCepikGroupLabel,
    sortCepikVersions,
    openCepikDataRejectionModal,
  } = useNewCarSelect(props);

  const {
    handleScroll,
    tryScrollToInfoEkspert,
    scrollIfNeeded,
  } = useNewCarSelectScroll(value);

  const horsePowerIndex = isMotor ? 5 : 6;
  const infoExpertIndex = isMotor ? 7 : 8;

  const isInfoekspertActiveQuestion = [
    FieldsEnum.CarModelIe,
    FieldsEnum.CarVersionIe,
  ].includes(activeQuestion.key);

  const shouldShowTooltip =
    formName !== FormsEnum.CompareCepikDataForm &&
    !(formName === FormsEnum.CalculationForm && isFormMicroCenterActive);

  const FormRowContainer =
    formName === FormsEnum.CalculationForm && isOperator
      ? CepikFormRowContainer
      : DefaultFormRowContainer;

  const renderTags = (
    values: Dictionary,
    getTagProps: AutocompleteGetTagProps
  ) => {
    const isMakeModelYearCombined = isCepik3ShortPath;

    return (
      <>
        <div
          aria-label={'basic'}
          className={classes.popperAnchor}
          ref={!isInfoekspertActiveQuestion ? popperAnchorRef : undefined}
        >
          {isMakeModelYearCombined &&
            values.length >= 3 &&
            (() => {
              const tagProps = getTagProps({ index: 0 }) as TagProps;
              const label = `${value[1].value} ${value[2].value}, ${value[0].value} r.`;

              return (
                <Badge
                  aria-label={label}
                  label={
                    <>
                      <Check className={classes.badgeCheck} />
                      <span>{label}</span>
                    </>
                  }
                  {...tagProps}
                  onDelete={openCepikDataRejectionModal}
                  className={classNames(
                    tagProps.className,
                    classes.badgeCombined
                  )}
                />
              );
            })()}

          {values
            .slice(isMakeModelYearCombined ? 3 : 0, infoExpertIndex)
            .map((option, i) => {
              const index = isMakeModelYearCombined ? i + 3 : i;

              const tagProps = getTagProps({ index }) as TagProps;
              const readonly = cepikModalControl && index < 3;

              if (index === horsePowerIndex && hideHorsePowerOptions) {
                return null;
              }

              return (
                <Badge
                  aria-label={option.value}
                  label={option.value}
                  readonly={readonly}
                  {...tagProps}
                  onClick={
                    onTagClick
                      ? (e) => {
                          e.stopPropagation();
                          onTagClick(option);
                          handleScroll(e);
                        }
                      : undefined
                  }
                  onDelete={(e) => {
                    handleScroll(e);
                    tagProps.onDelete();
                  }}
                  onFocus={onTagFocus ? () => onTagFocus(option) : undefined}
                  className={classNames(tagProps.className, {
                    [classes.badgeWithBorder]: isFormMicroCenterActive,
                  })}
                />
              );
            })}
        </div>
        {values.length >= infoExpertIndex && (
          <div className={classes.separator} aria-label={'Infoekspert header'}>
            {LabelEnum.InfoekspertHeading}
          </div>
        )}
        {values.length > infoExpertIndex && (
          <>
            <div
              className={classes.popperAnchor}
              aria-label={'Info ekspert'}
              ref={isInfoekspertActiveQuestion ? popperAnchorRef : undefined}
              onClick={(e) => {
                e.stopPropagation();
                tryScrollToInfoEkspert(e);
              }}
            >
              {values.slice(infoExpertIndex).map((option, index) => {
                index = index + infoExpertIndex;
                const tagProps = getTagProps({ index }) as TagProps;

                return (
                  <Badge
                    aria-label={option.value}
                    label={option.value}
                    {...tagProps}
                    onClick={
                      onTagClick
                        ? (e) => {
                            e.stopPropagation();
                            tryScrollToInfoEkspert(e);
                            onTagClick(option);
                          }
                        : undefined
                    }
                    onFocus={onTagFocus ? () => onTagFocus(option) : undefined}
                    className={classNames(tagProps.className, {
                      [classes.badgeWithBorder]: isFormMicroCenterActive,
                    })}
                  />
                );
              })}
            </div>
          </>
        )}
      </>
    );
  };

  const renderGroup = (data: {
    key: number;
    group: string;
    children: JSX.Element;
  }) => {
    if (data.group !== 'dictionary') {
      return <span key={`${data.key}`}>{data.children}</span>;
    }

    return (
      <span key={`${data.group}-expander`}>
        <div
          className={classes.expand}
          onClick={() => setGroupExpanded(!groupExpanded)}
        >
          {groupExpanded ? (
            <>
              <div>{LabelEnum.CollapseOptions}</div>
              <ChevronUp className={classes.expandIcon} />
            </>
          ) : (
            <>
              <div>{LabelEnum.More}</div>
              <ChevronDown className={classes.expandIcon} />
            </>
          )}
        </div>
        {groupExpanded && <>{data.children}</>}
      </span>
    );
  };

  const renderCepikModalGroup = (data: {
    key: number;
    group: string;
    children: JSX.Element;
  }) => {
    const { group } = data;

    if (group === 'dictionary' || group === '') {
      return renderGroup(data);
    } else if (group.startsWith('cepik')) {
      const childrenArray = sortCepikVersions(
        React.Children.toArray(data.children)
      );

      return group === 'cepik-form' ? (
        <span key={`${activeQuestion.key}-cepik-form-label`}>
          <li className={classes.versionsListHeader}>
            {getCepikGroupLabel(activeQuestion.key, 'form')}
          </li>
          {childrenArray}
        </span>
      ) : (
        <span key={`${activeQuestion.key}-cepik-label`}>
          <li className={classNames(classes.versionsListHeader, classes.green)}>
            {getCepikGroupLabel(activeQuestion.key, 'cepik')}
          </li>
          {childrenArray}
        </span>
      );
    }
  };

  const CustomPopper = (props: PropsWithChildren<PopperProps>) => {
    const hasFocusedInput = popperAnchorNode?.parentNode?.querySelector(
      `input:focus`
    );

    return (
      <Popper
        {...props}
        anchorEl={(!hasFocusedInput && popperAnchorNode) || props.anchorEl}
        modifiers={{
          flip: {
            enabled: false,
          },
        }}
        container={() =>
          document.querySelector('.modal-opened') || document.body
        }
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        placement={'bottom-start'}
        children={props.children}
      />
    );
  };

  return (
    <div
      className={classNames(
        classes.root,
        {
          [classes.rootNoSpacing]:
            formName === FormsEnum.CalculationForm && isFormMicroCenterActive,
        },
        className
      )}
    >
      <FormRowContainer
        data-field-error={error ? 'true' : undefined}
        data-field-type="question"
        formName={formName}
      >
        <ReactPlaceholder
          ready={everyFieldInitialized}
          customPlaceholder={<InputPlaceholder width={'full'} />}
        >
          <ControlContainer
            inputId={`car-select-control`}
            width={'full'}
            hoverTooltipText={
              showFillPreviousQuestionsMessage
                ? LabelEnum.FillPreviousQuestions
                : ''
            }
            tooltip={
              shouldShowTooltip && (
                <CarSelectControlTooltip formName={formName} />
              )
            }
          >
            <FormControl
              fullWidth
              onClick={
                isMobileOrTablet() && isSafariOrIOS()
                  ? scrollIfNeeded
                  : handleScroll
              }
              disabled={showFillPreviousQuestionsMessage}
            >
              <StepSelectControl
                options={options}
                groupBy={groupBy}
                onChange={onChange}
                label={LabelEnum.Vehicle}
                renderOption={activeQuestion.option}
                renderInputIcon={activeQuestion.inputIcon}
                inputPlaceholder={question.label as LabelEnum}
                inputHidden={!open && isLastValueSelected}
                value={showFillPreviousQuestionsMessage ? null : value}
                loading={loading}
                open={open}
                onOpen={onOpen}
                onBlur={onBlur}
                error={error}
                endAdornmentHidden
                CustomPopper={CustomPopper}
                renderTags={
                  showFillPreviousQuestionsMessage ? undefined : renderTags
                }
                renderGroup={
                  cepikModalControl
                    ? renderCepikModalGroup
                    : activeQuestion.key === FieldsEnum.CarVersionEt
                    ? renderGroup
                    : undefined
                }
                openOnFocus={!isLastValueSelected}
                disabled={showFillPreviousQuestionsMessage}
              />
            </FormControl>
          </ControlContainer>
          {!showFillPreviousQuestionsMessage && (
            <FormRowValidationMessage message={message} />
          )}
          {!showFillPreviousQuestionsMessage &&
            messages
              .filter((msg) => !msg.key.includes('$required'))
              .map((msg) => (
                <FormRowValidationMessage message={msg} key={msg.key} />
              ))}
          <CarDetailsEtList />
        </ReactPlaceholder>
      </FormRowContainer>
    </div>
  );
}
